import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Copy, Database, Ellipsis, History as HistoryIcon, LayoutDashboard, Link2, Loader2, Pencil, RotateCcw,
  Smartphone, Sparkles, X,
} from 'lucide-react';

import { useXomicsAuth } from './AuthContext';
import {
  fetchPatientData,
  injectPatientExampleData,
  loadMobileAccounts,
  loadPatientDataAvailability,
  loadPatientDataTypes,
  loadPatientExampleDataBatches,
  rollbackPatientExampleData,
  setPatientResultRowHidden,
} from './api';
import {
  DOMAINS, PatientDataVisual, PatientOverview, PatientTrendVisual, PatientVisualBoundary, ResultRowHiding,
  codeWords, dataRecordLabel, dataTypeIcon, domainOf,
} from './PatientDataVisuals';
import { PatientHistory } from './PatientHistory';
import { Pill, SearchField, WorkspaceTabs } from './parts';
import { tx } from './i18n';
import { errorMessage } from './ui';
import { counted, initials } from './MobileParts';
import { PatientAppLinkDialog } from './PatientAppLink';
import { PatientEditDialog } from './PatientEdit';
import './patient.css';

// One patient's whole record: an overview of every kind of data they have, then
// each kind on its own, a visit at a time or as a trend across visits. Drawn with
// the Mobile app section's parts, in a dialog over whichever page opened it.

const OVERVIEW = 'overview';
// Everything done that concerns the patient (PatientHistory.js), beside the overview.
const HISTORY = 'history';
const FETCH_BATCH_SIZE = 100;

function displayDate(value) {
  const timestamp = Number(value);
  if (!Number.isFinite(timestamp) || timestamp <= 0) return tx('ui.patient.not_recorded');
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(timestamp));
}

function resultTime(result) {
  return Number(result.uploadedAt || result.observedAt || result.recordedAt || 0);
}

function selectionFor(dataTypeCode, result) {
  if (result.kind === 'medical_record') {
    return { dataTypeCode, medicalRecordId: Number(result.medicalRecordId) };
  }
  if (result.kind === 'omics_job') {
    return { dataTypeCode, jobId: Number(result.jobId) };
  }
  throw new Error(`Unsupported patient result kind: ${result.kind}`);
}

function resultIdentity(result) {
  if (result.kind === 'medical_record') return `medical:${Number(result.medicalRecordId)}`;
  if (result.kind === 'omics_job') return `omics:${Number(result.jobId)}`;
  throw new Error(`Unsupported patient result kind: ${result.kind}`);
}

function entryIdentity(entry) {
  if (entry?.selection?.medicalRecordId !== undefined) {
    return `medical:${Number(entry.selection.medicalRecordId)}`;
  }
  if (entry?.selection?.jobId !== undefined) return `omics:${Number(entry.selection.jobId)}`;
  throw new Error('Patient data result has no record identity');
}

function latestResult(type) {
  const results = Array.isArray(type?.results) ? type.results : [];
  // Availability is ascending by observation/visit and job id. The last result
  // is therefore the current record and, for duplicate jobs, the newest job.
  return results.length === 0 ? null : results[results.length - 1];
}

function comparableResults(type) {
  const results = Array.isArray(type?.results) ? type.results : [];
  if (type?.dataType?.sourceType === 'medical_record') {
    return results.filter((result) => Number.isFinite(Number(
      result.observedAt || result.recordedAt || result.uploadedAt,
    )));
  }

  // The trend contract accepts one job for a data type and visit. If historical
  // imports left several completed jobs on one visit, keep the greatest job id.
  const newestByVisit = new Map();
  results.forEach((result) => {
    const visit = Number(result.visitNumber);
    if (!Number.isSafeInteger(visit)) return;
    const current = newestByVisit.get(visit);
    if (!current || Number(result.jobId) > Number(current.jobId)) {
      newestByVisit.set(visit, result);
    }
  });
  return [...newestByVisit.entries()]
    .sort(([left], [right]) => left - right)
    .map(([, result]) => result);
}

function typeMatches(type, query) {
  const needle = query.trim().toLocaleLowerCase();
  if (needle === '') return true;
  return [
    type.dataType.displayName,
    type.dataType.description,
    type.dataType.category,
    type.dataType.subcategory,
    type.dataType.code,
  ].some((value) => String(value || '').toLocaleLowerCase().includes(needle));
}

// Laboratory records arrive one per panel, so they are picked as a panel and then its visit.
function panelOf(result) {
  return result.panelName || result.panelCode || null;
}

// Where a result came from, in words: its sample, the system that recorded it, or its kind.
function sourceOf(result) {
  return result.sampleCode || codeWords(result.sourceSystem)
    || tx(result.kind === 'omics_job' ? 'ui.patient.omics_result' : 'ui.patient.clinical_record');
}

function RecordHead({ type, result }) {
  const Icon = dataTypeIcon(type.dataType);
  return (
    <header className="xo-rec-type-head">
      <span className="xo-rec-type-icon"><Icon aria-hidden="true" /></span>
      <div className="xo-rec-type-text">
        <span>{[codeWords(type.dataType.category), codeWords(type.dataType.subcategory)].filter(Boolean).join(' · ')}</span>
        <h3>{type.dataType.displayName}</h3>
        {type.dataType.description && <p>{type.dataType.description}</p>}
      </div>
      <dl className="xo-rec-type-facts">
        <div><dt>{tx('ui.patient.record')}</dt><dd>{dataRecordLabel(result)}</dd></div>
        <div><dt>{tx('ui.patient.recorded')}</dt><dd>{displayDate(resultTime(result))}</dd></div>
        <div><dt>{tx('ui.patient.source')}</dt><dd>{sourceOf(result)}</dd></div>
      </dl>
    </header>
  );
}

function SyntheticActions({ batches, busy, onInject, onRollback }) {
  const ready = batches.filter(({ status }) => status === 'READY');
  return (
    <div className="xo-rec-synthetic">
      <div className="xo-rec-synthetic-intro">
        <Sparkles aria-hidden="true" />
        <span><strong>{tx('ui.patient.synthetic_title')}</strong><small>{tx('ui.patient.synthetic_detail')}</small></span>
      </div>
      <div className="xo-rec-synthetic-profiles">
        {['LOW', 'MEDIUM', 'HIGH'].map((profile) => (
          <button key={profile} type="button" disabled={busy || ready.length > 0} onClick={() => onInject(profile)}>
            {busy ? <Loader2 className="xo-spin" aria-hidden="true" /> : tx(`ui.patient.synthetic_${profile.toLowerCase()}`)}
          </button>
        ))}
      </div>
      {ready.map((batch) => (
        <article key={batch.uuid}>
          <span>
            <strong>{tx('ui.patient.synthetic_batch', { profile: tx(`ui.patient.synthetic_${String(batch.profile).toLowerCase()}`) })}</strong>
            <small>{tx('ui.patient.synthetic_batch_detail', { visits: batch.counts?.visits || 0, jobs: batch.counts?.jobs || 0 })}</small>
          </span>
          <button type="button" disabled={busy} onClick={() => onRollback(batch.uuid)}><RotateCcw aria-hidden="true" />{tx('ui.patient.synthetic_remove')}</button>
        </article>
      ))}
    </div>
  );
}

function Shimmer({ className = '' }) {
  return <span className={`xo-rec-shimmer ${className}`.trim()} aria-hidden="true" />;
}

function PatientModalSkeleton() {
  return (
    <div className="xo-rec-body" role="status" aria-live="polite" aria-label={tx('ui.patient.loading_record')}>
      <nav className="xo-rec-rail" aria-hidden="true">
        <Shimmer className="is-block" />
        {[0, 1, 2].map((group) => (
          <div className="xo-rec-rail-group" key={group}>
            <Shimmer className="is-label" />
            {[0, 1, 2].map((row) => <Shimmer className="is-row" key={row} />)}
          </div>
        ))}
      </nav>
      <main className="xo-rec-stage" aria-hidden="true">
        <div className="xo-rec-skeleton-hero"><Shimmer className="is-panel" /><Shimmer className="is-panel is-dark" /></div>
        <div className="xo-rec-skeleton-grid">{[0, 1, 2, 3].map((item) => <Shimmer className="is-panel" key={item} />)}</div>
      </main>
    </div>
  );
}

function PatientRecordSkeleton() {
  return (
    <div className="xo-rec-record-skeleton" role="status" aria-live="polite" aria-label={tx('ui.patient.loading_record')}>
      <Shimmer className="is-strip" />
      <Shimmer className="is-panel" />
      <Shimmer className="is-panel" />
    </div>
  );
}

function Failure({ message, onRetry }) {
  return (
    <div className="xo-rec-error" role="alert">
      <Database aria-hidden="true" />
      <span>{message}</span>
      {onRetry && <button type="button" onClick={onRetry}>{tx('ui.patient.try_again')}</button>}
    </div>
  );
}

// Every active app account that belongs to the patient (one patient may have
// several), and every one still waiting on its invitation, each opening that
// account, for staff who may see app accounts. A few are shown; the rest open in
// place. Read again when the record links an account (revision).
const APP_ACCOUNTS_SHOWN = 3;
const APP_ACCOUNT_STATUSES = ['active', 'invited'];

function AppAccounts({ patientId, revision, onOpen }) {
  const { permissions } = useXomicsAuth();
  const mayRead = permissions.includes('PLATFORM_MOBILE_READ');
  const [accounts, setAccounts] = useState([]);
  const [failed, setFailed] = useState(false);
  const [all, setAll] = useState(false);

  useEffect(() => {
    setAccounts([]);
    setFailed(false);
    setAll(false);
  }, [patientId]);

  useEffect(() => {
    if (!mayRead) return undefined;
    let live = true;
    loadMobileAccounts({ patient: patientId, limit: 100 })
      .then((page) => {
        if (!live) return;
        setAccounts(page.accounts.filter((account) => APP_ACCOUNT_STATUSES.includes(account.status)));
        setFailed(false);
      })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [mayRead, patientId, revision]);

  if (failed) return <p className="xo-rec-apps-failed" role="alert">{tx('ui.patient.app_accounts_failed')}</p>;
  if (accounts.length === 0) return null;
  const hidden = accounts.length - APP_ACCOUNTS_SHOWN;
  return (
    <>
      <ul className={`xo-rec-apps${all ? ' is-all' : ''}`} aria-label={tx('ui.patient.app_accounts')}>
        {(all ? accounts : accounts.slice(0, APP_ACCOUNTS_SHOWN)).map((account) => (
          <li key={account.uuid}>
            <Link to={`/xomics/mobile/accounts?account=${account.uuid}`} onClick={onOpen}
              className={account.status === 'invited' ? 'is-invited' : undefined}
              title={`${tx('ui.patient.open_app_account')}: ${account.email || account.uuid}`}>
              <Smartphone aria-hidden="true" />{account.email || account.uuid}
              {account.status === 'invited' && <Pill>{tx('ui.mobile.status_invited')}</Pill>}
            </Link>
          </li>
        ))}
      </ul>
      {hidden > 0 && (
        <button type="button" className="xo-rec-apps-more" aria-expanded={all} onClick={() => setAll((open) => !open)}>
          {all ? tx('ui.mobile.show_fewer') : tx('ui.mobile.and_more', { count: hidden })}
        </button>
      )}
    </>
  );
}

// onEdited (optional) tells the page that opened the record that the patient's
// details changed, so it reads its own list again.
export function PatientDetailModal({ patient, mayManageExamples, onClose, onEdited }) {
  const { permissions } = useXomicsAuth();
  // Editing the patient's details, and linking an app account to them, found or
  // invited, are patient identity work.
  const mayManageIdentity = permissions.includes('PATIENT_IDENTITY_MANAGE');
  // Hiding a row of a result, such as a variant, from reports, the app and the
  // digital twin. Whoever may do it also sees the hidden rows, to show them again.
  const mayHideRows = permissions.includes('PATIENT_RESULT_HIDE');
  const [hidingBusy, setHidingBusy] = useState(() => new Set());
  const [hidingError, setHidingError] = useState('');
  const [historyRevision, setHistoryRevision] = useState(0);
  const [linking, setLinking] = useState(false);
  const [editing, setEditing] = useState(false);
  // What an edit made here changed, shown at once: the page that opened the
  // record still holds the patient as it was.
  const [edited, setEdited] = useState(null);
  const [appAccountsRevision, setAppAccountsRevision] = useState(0);
  const [availability, setAvailability] = useState(null);
  const [selectedCode, setSelectedCode] = useState(OVERVIEW);
  const [selectedRecord, setSelectedRecord] = useState(0);
  const [viewMode, setViewMode] = useState('record');
  const [entry, setEntry] = useState(null);
  const [trendEntries, setTrendEntries] = useState([]);
  const [overviewEntries, setOverviewEntries] = useState({});
  const [overviewLoadingCodes, setOverviewLoadingCodes] = useState([]);
  const [overviewFailedCodes, setOverviewFailedCodes] = useState({});
  const [batches, setBatches] = useState([]);
  const [query, setQuery] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuView, setMenuView] = useState('root');
  const [loading, setLoading] = useState(true);
  const [recordLoading, setRecordLoading] = useState(false);
  const [trendLoading, setTrendLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [recordError, setRecordError] = useState('');
  const [trendError, setTrendError] = useState('');

  const menuRef = useRef(null);
  const stageRef = useRef(null);
  const controlsRef = useRef(null);
  const loadGeneration = useRef(0);
  const selectedCodeRef = useRef(OVERVIEW);

  const loadOverviewType = useCallback(async (type, generation) => {
    const code = type.dataType.code;
    const current = latestResult(type);
    if (generation !== loadGeneration.current) return;
    if (!current) {
      setOverviewFailedCodes((failed) => ({ ...failed, [code]: tx('ui.patient.no_current_result') }));
      setOverviewLoadingCodes((codes) => codes.filter((candidate) => candidate !== code));
      return;
    }
    try {
      const results = await fetchPatientData(patient.id, [selectionFor(code, current)]);
      if (results.length !== 1) throw new Error(tx('ui.patient.current_result_missing'));
      if (generation !== loadGeneration.current) return;
      setOverviewEntries((entries) => ({ ...entries, [code]: results[0] }));
    } catch (caught) {
      if (generation !== loadGeneration.current) return;
      setOverviewFailedCodes((failed) => ({ ...failed, [code]: errorMessage(caught) }));
    } finally {
      if (generation === loadGeneration.current) {
        setOverviewLoadingCodes((codes) => codes.filter((candidate) => candidate !== code));
      }
    }
  }, [patient.id]);

  const startOverviewLoad = useCallback((types, generation) => {
    setOverviewEntries({});
    setOverviewFailedCodes({});
    setOverviewLoadingCodes(types.map(({ dataType }) => dataType.code));

    // Medical payloads are compact and can establish the clinical baseline in
    // parallel. Omics jobs can contain many rows, so those arrive one at a time.
    types.filter(({ dataType }) => dataType.sourceType === 'medical_record')
      .forEach((type) => { void loadOverviewType(type, generation); });
    const omics = types.filter(({ dataType }) => dataType.sourceType !== 'medical_record');
    void (async () => {
      for (const type of omics) {
        if (generation !== loadGeneration.current) return;
        await loadOverviewType(type, generation);
      }
    })();
  }, [loadOverviewType]);

  const load = useCallback(async () => {
    const generation = loadGeneration.current + 1;
    loadGeneration.current = generation;
    setLoading(true);
    setError('');
    setRecordError('');
    setTrendError('');
    setTrendEntries([]);
    try {
      const catalogue = await loadPatientDataTypes();
      const [available, exampleBatches] = await Promise.all([
        loadPatientDataAvailability(patient.id, catalogue.map(({ code }) => code)),
        mayManageExamples ? loadPatientExampleDataBatches(patient.id) : Promise.resolve([]),
      ]);
      if (generation !== loadGeneration.current) return;

      const populated = available.dataTypes.filter(({ results }) => results.length > 0);
      setAvailability({ ...available, dataTypes: populated });
      setBatches(exampleBatches);

      const previousCode = selectedCodeRef.current;
      const previousType = populated.find(({ dataType }) => dataType.code === previousCode);
      const nextCode = previousCode === HISTORY ? HISTORY : previousCode === OVERVIEW || !previousType ? OVERVIEW : previousCode;
      selectedCodeRef.current = nextCode;
      setSelectedCode(nextCode);
      setSelectedRecord(previousType ? Math.max(0, previousType.results.length - 1) : 0);
      setViewMode('record');
      startOverviewLoad(populated, generation);
    } catch (caught) {
      if (generation === loadGeneration.current) setError(errorMessage(caught));
    } finally {
      if (generation === loadGeneration.current) setLoading(false);
    }
  }, [mayManageExamples, patient.id, startOverviewLoad]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { loadGeneration.current += 1; }, []);
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, []);
  useEffect(() => {
    document.getElementById('xo-patient-data-search')?.focus({ preventScroll: true });
  }, []);
  useEffect(() => { setEdited(null); }, [patient.id]);
  useEffect(() => {
    const close = (event) => {
      // The link and edit dialogs close themselves first.
      if (event.key !== 'Escape' || linking || editing) return;
      if (menuOpen) {
        setMenuOpen(false);
        setMenuView('root');
        return;
      }
      onClose();
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [editing, linking, menuOpen, onClose]);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (event) => {
      if (menuRef.current?.contains(event.target)) return;
      setMenuOpen(false);
      setMenuView('root');
    };
    window.addEventListener('mousedown', close);
    return () => window.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const availableTypes = useMemo(() => availability?.dataTypes || [], [availability]);
  const visibleTypes = useMemo(
    () => availableTypes.filter((type) => typeMatches(type, query)),
    [availableTypes, query],
  );
  const selectedType = useMemo(() => availableTypes.find(
    ({ dataType }) => dataType.code === selectedCode,
  ) || null, [availableTypes, selectedCode]);
  const selectedResult = selectedType?.results[selectedRecord] || null;
  const panels = useMemo(() => {
    const results = selectedType?.results || [];
    if (!results.some(panelOf)) return null;
    const byName = new Map();
    results.forEach((result, index) => {
      const name = panelOf(result) || tx('ui.patient.not_recorded');
      if (!byName.has(name)) byName.set(name, []);
      byName.get(name).push(index);
    });
    return [...byName.entries()].map(([name, indexes]) => ({ name, indexes }));
  }, [selectedType]);
  const currentPanel = panels ? panels.find((panel) => panel.indexes.includes(selectedRecord)) || null : null;
  const visitIndexes = currentPanel ? currentPanel.indexes : (selectedType ? selectedType.results.map((_result, index) => index) : []);
  const visitPosition = visitIndexes.indexOf(selectedRecord);
  const trendResults = useMemo(() => comparableResults(selectedType), [selectedType]);
  const hasTrend = trendResults.length > 1;
  // Visits, not records: a laboratory visit holds one record per panel.
  const trendVisits = useMemo(() => new Set(trendResults.map((result) => (
    Number.isSafeInteger(Number(result.visitNumber)) ? `visit:${result.visitNumber}` : new Date(resultTime(result)).toISOString().slice(0, 10)
  ))).size, [trendResults]);
  const distinctVisitCount = useMemo(() => {
    const linkedVisits = new Set();
    const unlinkedOmicsVisits = new Set();
    const unlinkedMedicalDates = new Set();
    availableTypes.forEach(({ results }) => results.forEach((result) => {
      const clinicBookingId = Number(result.clinicBookingId);
      if (Number.isSafeInteger(clinicBookingId) && clinicBookingId > 0) {
        linkedVisits.add(clinicBookingId);
        return;
      }
      if (result.kind === 'omics_job') {
        const visit = Number(result.visitNumber);
        if (Number.isSafeInteger(visit) && visit > 0) unlinkedOmicsVisits.add(visit);
        return;
      }
      if (result.kind === 'medical_record') {
        const observedAt = Number(result.observedAt || result.recordedAt);
        if (Number.isFinite(observedAt) && observedAt > 0) {
          unlinkedMedicalDates.add(new Date(observedAt).toISOString().slice(0, 10));
        }
      }
    }));
    // Synthetic records have no booking id, but their medical and omics rows
    // describe the same visits. Taking the larger series avoids counting both.
    return linkedVisits.size + Math.max(unlinkedOmicsVisits.size, unlinkedMedicalDates.size);
  }, [availableTypes]);
  const recordCount = useMemo(() => availableTypes.reduce(
    (sum, type) => sum + type.results.length, 0,
  ), [availableTypes]);
  const latestAt = useMemo(() => Math.max(0, ...availableTypes.flatMap(({ results }) => results.map(resultTime))), [availableTypes]);

  const scrollStageTop = () => stageRef.current?.scrollTo({ top: 0 });

  // The chosen panel and visit stay in sight when there are more than fit.
  useEffect(() => {
    controlsRef.current?.querySelectorAll('.is-active').forEach((node) => node.scrollIntoView?.({ block: 'nearest', inline: 'center' }));
  }, [selectedRecord, selectedCode, viewMode]);

  const openOverview = () => {
    selectedCodeRef.current = OVERVIEW;
    setSelectedCode(OVERVIEW);
    setSelectedRecord(0);
    setViewMode('record');
    setRecordError('');
    setTrendError('');
    scrollStageTop();
  };

  const openHistory = () => {
    selectedCodeRef.current = HISTORY;
    setSelectedCode(HISTORY);
    setSelectedRecord(0);
    setViewMode('record');
    scrollStageTop();
  };

  // Opens a data type at its latest record, or at the record the overview's
  // omics map asked for.
  const openType = (type, recordIndex = null) => {
    selectedCodeRef.current = type.dataType.code;
    setSelectedCode(type.dataType.code);
    const last = Math.max(0, type.results.length - 1);
    setSelectedRecord(Number.isSafeInteger(recordIndex) && recordIndex >= 0 && recordIndex <= last ? recordIndex : last);
    setViewMode('record');
    setRecordError('');
    setTrendError('');
    setTrendEntries([]);
    scrollStageTop();
  };

  const openTypeByCode = (code, recordIndex = null) => {
    const type = availableTypes.find(({ dataType }) => dataType.code === code);
    if (type) openType(type, recordIndex);
  };

  useEffect(() => {
    let cancelled = false;
    if (!selectedType || !selectedResult) {
      setEntry(null);
      setRecordLoading(false);
      return undefined;
    }
    setEntry(null);
    setRecordLoading(true);
    setRecordError('');
    setHidingError('');
    fetchPatientData(patient.id, [selectionFor(selectedType.dataType.code, selectedResult)], { includeHidden: mayHideRows })
      .then((results) => {
        if (cancelled) return;
        if (results.length !== 1) throw new Error(tx('ui.patient.record_missing'));
        setEntry(results[0]);
      })
      .catch((caught) => { if (!cancelled) setRecordError(errorMessage(caught)); })
      .finally(() => { if (!cancelled) setRecordLoading(false); });
    return () => { cancelled = true; };
  }, [mayHideRows, patient.id, selectedResult, selectedType]);

  // Hides or shows one row, then shows it as it now is here and in the overview,
  // which reads what reports and the app read.
  const toggleRowHidden = useCallback(async (jobOutputId, row) => {
    const key = `${jobOutputId}:${row.sourceRow}`;
    const hidden = row.hidden !== true;
    setHidingBusy((busy) => new Set(busy).add(key));
    setHidingError('');
    try {
      await setPatientResultRowHidden(patient.id, jobOutputId, row.sourceRow, hidden);
      setEntry((current) => (current && {
        ...current,
        data: current.data.map((output) => (Number(output.jobOutputId) !== Number(jobOutputId) ? output : {
          ...output,
          rows: output.rows.map((candidate) => (candidate.sourceRow === row.sourceRow ? { ...candidate, hidden } : candidate)),
        })),
      }));
      if (selectedType) void loadOverviewType(selectedType, loadGeneration.current);
      setHistoryRevision((revision) => revision + 1);
    } catch (caught) {
      setHidingError(errorMessage(caught));
    } finally {
      setHidingBusy((busy) => {
        const next = new Set(busy);
        next.delete(key);
        return next;
      });
    }
  }, [loadOverviewType, patient.id, selectedType]);
  const hiding = useMemo(() => (mayHideRows ? { toggle: toggleRowHidden, busy: hidingBusy } : null), [hidingBusy, mayHideRows, toggleRowHidden]);

  useEffect(() => {
    if (viewMode !== 'trend' || !selectedType || trendResults.length < 2) return undefined;
    let cancelled = false;
    setTrendEntries([]);
    setTrendLoading(true);
    setTrendError('');

    (async () => {
      try {
        const byIdentity = new Map();
        for (let start = 0; start < trendResults.length; start += FETCH_BATCH_SIZE) {
          const batch = trendResults.slice(start, start + FETCH_BATCH_SIZE);
          const fetched = await fetchPatientData(patient.id, batch.map((result) => (
            selectionFor(selectedType.dataType.code, result)
          )));
          fetched.forEach((result) => byIdentity.set(entryIdentity(result), result));
        }
        const ordered = trendResults.map((result) => byIdentity.get(resultIdentity(result)));
        if (ordered.some((result) => !result)) throw new Error(tx('ui.patient.trend_records_missing'));
        if (!cancelled) setTrendEntries(ordered);
      } catch (caught) {
        if (!cancelled) setTrendError(errorMessage(caught));
      } finally {
        if (!cancelled) setTrendLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [patient.id, selectedType, trendResults, viewMode]);

  const mutate = async (operation) => {
    setBusy(true);
    setError('');
    try {
      await operation();
      setMenuOpen(false);
      setMenuView('root');
      await load();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  // Opened from a mobile account the subproject arrives as its code; from a project, as the subproject.
  const subProject = typeof patient.subProject === 'string' ? patient.subProject : patient.subProject?.code;
  const person = useMemo(() => (edited === null ? patient : { ...patient, ...edited }), [patient, edited]);
  const name = person.name || tx('ui.patient.patient_n', { id: patient.id });

  return (
    <div className="xo-rec-layer" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <motion.section className="xo-rec" role="dialog" aria-modal="true" aria-labelledby="xo-patient-record-title"
        initial={{ opacity: 0, y: 18, scale: 0.99 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.22, ease: 'easeOut' }}>
        <header className="xo-rec-head">
          <div className="xo-rec-identity">
            <span className="xo-rec-avatar">{initials(person.name, null) === '?' ? 'P' : initials(person.name, null)}</span>
            <div>
              <span>{tx('ui.patient.record_eyebrow')}</span>
              <h2 id="xo-patient-record-title">{name}</h2>
              <p>{[tx('ui.patient.patient_n', { id: patient.id }), subProject || tx('ui.patient.no_subproject')].join(' · ')}</p>
              <AppAccounts patientId={patient.id} revision={appAccountsRevision} onOpen={onClose} />
            </div>
          </div>
          <SearchField className="xo-rec-search" id="xo-patient-data-search"
            value={query} onChange={setQuery} name="patient-data-search"
            placeholder={tx('ui.patient.search_placeholder')} label={tx('ui.patient.search_label')} autoComplete="off" />
          <dl className="xo-rec-stats">
            <div><dt>{tx('ui.patient.data_types')}</dt><dd>{loading ? '—' : availableTypes.length}</dd></div>
            <div><dt>{tx('ui.patient.records')}</dt><dd>{loading ? '—' : recordCount}</dd></div>
            <div><dt>{tx('ui.patient.visits')}</dt><dd>{loading ? '—' : distinctVisitCount}</dd></div>
            <div><dt>{tx('ui.patient.latest_record')}</dt><dd className="is-date">{loading || latestAt === 0 ? '—' : displayDate(latestAt)}</dd></div>
          </dl>
          <div className="xo-rec-head-actions">
            {mayManageIdentity && (
              <button type="button" className="xo-rec-head-button" onClick={() => { setMenuOpen(false); setMenuView('root'); setEditing(true); }}
                aria-label={tx('ui.patient.edit_details')} title={tx('ui.patient.edit_details')}><Pencil aria-hidden="true" /></button>
            )}
            {(mayManageExamples || mayManageIdentity) && (
              <div className="xo-rec-more-wrap" ref={menuRef}>
                <button type="button" className="xo-rec-head-button" aria-haspopup="menu" aria-expanded={menuOpen}
                  aria-label={tx('ui.patient.more_options')} title={tx('ui.patient.more_options')}
                  onClick={() => { setMenuOpen((open) => !open); setMenuView('root'); }}><Ellipsis aria-hidden="true" /></button>
                {menuOpen && menuView === 'root' && (
                  <div className="xo-rec-menu" role="menu">
                    <header><strong>{tx('ui.patient.patient_actions')}</strong><small>{tx('ui.patient.patient_actions_detail')}</small></header>
                    {mayManageIdentity && (
                      <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); setMenuView('root'); setLinking(true); }}>
                        <Link2 aria-hidden="true" /><span><strong>{tx('ui.patient.link_app_account')}</strong><small>{tx('ui.patient.link_app_account_detail')}</small></span><ChevronRight aria-hidden="true" />
                      </button>
                    )}
                    {mayManageExamples && (
                      <button type="button" role="menuitem" onClick={() => setMenuView('synthetic')}>
                        <Sparkles aria-hidden="true" /><span><strong>{tx('ui.patient.synthetic_data')}</strong><small>{tx('ui.patient.synthetic_data_detail')}</small></span><ChevronRight aria-hidden="true" />
                      </button>
                    )}
                    <button type="button" role="menuitem" onClick={() => { load(); setMenuOpen(false); }}>
                      <RotateCcw aria-hidden="true" /><span><strong>{tx('ui.patient.refresh_data')}</strong><small>{tx('ui.patient.refresh_data_detail')}</small></span><ChevronRight aria-hidden="true" />
                    </button>
                    <button type="button" role="menuitem" onClick={async () => {
                      await window.navigator.clipboard.writeText(String(patient.id));
                      setMenuOpen(false);
                    }}>
                      <Copy aria-hidden="true" /><span><strong>{tx('ui.patient.copy_id')}</strong><small>{tx('ui.patient.patient_n', { id: patient.id })}</small></span><ChevronRight aria-hidden="true" />
                    </button>
                  </div>
                )}
                {menuOpen && menuView === 'synthetic' && (
                  <div className="xo-rec-menu is-wide">
                    <button type="button" className="xo-rec-menu-back" onClick={() => setMenuView('root')}>
                      <ChevronLeft aria-hidden="true" />{tx('ui.patient.all_actions')}
                    </button>
                    <SyntheticActions batches={batches} busy={busy}
                      onInject={(profile) => mutate(() => injectPatientExampleData(patient.id, profile))}
                      onRollback={(uuid) => mutate(() => rollbackPatientExampleData(patient.id, uuid))} />
                  </div>
                )}
              </div>
            )}
            <button type="button" className="xo-rec-head-button" onClick={onClose} aria-label={tx('ui.patient.close_record')}><X aria-hidden="true" /></button>
          </div>
        </header>

        {error && <Failure message={error} onRetry={load} />}
        {loading ? <PatientModalSkeleton /> : (
          <div className="xo-rec-body">
            <nav className="xo-rec-rail" role="tablist" aria-orientation="vertical" aria-label={tx('ui.patient.data_types')}>
              <button type="button" className={`xo-rec-rail-overview${selectedCode === OVERVIEW ? ' is-active' : ''}`}
                role="tab" aria-selected={selectedCode === OVERVIEW} aria-controls="xo-patient-data-panel" onClick={openOverview}>
                <span className="xo-rec-rail-icon"><LayoutDashboard aria-hidden="true" /></span>
                <span><strong>{tx('ui.patient.overview')}</strong><small>{tx('ui.patient.overview_detail')}</small></span>
              </button>
              <button type="button" className={`xo-rec-rail-overview${selectedCode === HISTORY ? ' is-active' : ''}`}
                role="tab" aria-selected={selectedCode === HISTORY} aria-controls="xo-patient-data-panel" onClick={openHistory}>
                <span className="xo-rec-rail-icon"><HistoryIcon aria-hidden="true" /></span>
                <span><strong>{tx('ui.patient.activity')}</strong><small>{tx('ui.patient.activity_detail')}</small></span>
              </button>
              {DOMAINS.map((domain) => {
                const inDomain = visibleTypes.filter((type) => domainOf(type.dataType) === domain.key);
                if (inDomain.length === 0) return null;
                return (
                  <div key={domain.key} className="xo-rec-rail-group">
                    <h3><span>{tx(domain.labelKey)}</span><b>{inDomain.length}</b></h3>
                    {inDomain.map((type) => {
                      const active = type.dataType.code === selectedCode;
                      const Icon = dataTypeIcon(type.dataType);
                      const latest = latestResult(type);
                      return (
                        <button key={type.dataType.code} type="button" role="tab" aria-selected={active} aria-controls="xo-patient-data-panel"
                          className={active ? 'is-active' : ''} onClick={() => openType(type)}>
                          <span className="xo-rec-rail-icon"><Icon aria-hidden="true" /></span>
                          <span>
                            <strong>{type.dataType.displayName}</strong>
                            <small>{[counted('ui.patient.records_count', type.results.length), latest ? displayDate(resultTime(latest)) : null].filter(Boolean).join(' · ')}</small>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
              {availableTypes.length === 0 && (
                <div className="xo-rec-rail-empty"><Database aria-hidden="true" /><strong>{tx('ui.patient.no_data')}</strong><p>{tx('ui.patient.no_data_detail')}</p></div>
              )}
              {availableTypes.length > 0 && visibleTypes.length === 0 && (
                <div className="xo-rec-rail-empty"><Database aria-hidden="true" /><strong>{tx('ui.patient.no_type_match')}</strong><p>{tx('ui.patient.no_type_match_detail')}</p></div>
              )}
            </nav>
            <main className="xo-rec-stage" id="xo-patient-data-panel" role="tabpanel" ref={stageRef}>
              {selectedCode === OVERVIEW && (
                <PatientVisualBoundary resetKey={`${OVERVIEW}:${Object.keys(overviewEntries).length}:${overviewLoadingCodes.length}:${query}`}>
                  <PatientOverview availability={availability} entries={overviewEntries}
                    loadingCodes={overviewLoadingCodes} failedCodes={overviewFailedCodes}
                    onSelect={openTypeByCode} query={query} />
                </PatientVisualBoundary>
              )}
              {selectedCode === HISTORY && <PatientHistory patientId={patient.id} revision={historyRevision} />}
              {selectedCode !== OVERVIEW && selectedType && selectedResult && (
                <div className="xo-rec-type">
                  <RecordHead type={selectedType} result={selectedResult} />
                  {(hasTrend || selectedType.results.length > 1) && (
                    <div className="xo-rec-controls" ref={controlsRef}>
                      {viewMode === 'record' && selectedType.results.length > 1 ? (
                        <div className="xo-rec-picker">
                          {panels && panels.length > 1 && (
                            <div className="xo-rec-panels" role="tablist" aria-label={tx('ui.patient.panels')}>
                              {panels.map((panel) => (
                                <button key={panel.name} type="button" role="tab" aria-selected={panel === currentPanel}
                                  className={panel === currentPanel ? 'is-active' : ''}
                                  onClick={() => setSelectedRecord(panel.indexes[panel.indexes.length - 1])}>
                                  {panel.name}<b>{panel.indexes.length}</b>
                                </button>
                              ))}
                            </div>
                          )}
                          {visitIndexes.length > 1 && (
                            <div className="xo-rec-visits" aria-label={tx('ui.patient.records')}>
                              <button type="button" className="xo-rec-step" disabled={visitPosition <= 0} aria-label={tx('ui.patient.previous')}
                                onClick={() => setSelectedRecord(visitIndexes[visitPosition - 1])}><ChevronLeft aria-hidden="true" /></button>
                              <div className="xo-rec-visit-list">
                                {visitIndexes.map((index) => {
                                  const result = selectedType.results[index];
                                  return (
                                    <button key={resultIdentity(result)} type="button" className={index === selectedRecord ? 'is-active' : ''}
                                      aria-pressed={index === selectedRecord} onClick={() => setSelectedRecord(index)}>
                                      <strong>{dataRecordLabel(result)}</strong>
                                      <small>{displayDate(resultTime(result))}</small>
                                    </button>
                                  );
                                })}
                              </div>
                              <button type="button" className="xo-rec-step" disabled={visitPosition >= visitIndexes.length - 1} aria-label={tx('ui.patient.next')}
                                onClick={() => setSelectedRecord(visitIndexes[visitPosition + 1])}><ChevronRight aria-hidden="true" /></button>
                            </div>
                          )}
                        </div>
                      ) : <span className="xo-rec-controls-note">{tx('ui.patient.trend_across', { count: trendVisits })}</span>}
                      {hasTrend && (
                        <WorkspaceTabs value={viewMode} onChange={setViewMode} label={tx('ui.patient.result_view')} className="is-compact xo-rec-view-tabs" tabs={[
                          { value: 'record', label: tx('ui.patient.view_record') },
                          { value: 'trend', label: tx('ui.patient.view_trend') },
                        ]} />
                      )}
                    </div>
                  )}
                  <div className="xo-rec-visual" id="xo-patient-result-view">
                    {viewMode === 'record' && (
                      recordLoading ? <PatientRecordSkeleton />
                        : recordError ? <Failure message={recordError} />
                          : entry && (
                            <PatientVisualBoundary resetKey={`${selectedCode}:${selectedRecord}:${query}`}>
                              {hidingError && <Failure message={hidingError} />}
                              <ResultRowHiding.Provider value={hiding}>
                                <PatientDataVisual entry={entry} query={query} />
                              </ResultRowHiding.Provider>
                            </PatientVisualBoundary>
                          )
                    )}
                    {viewMode === 'trend' && (
                      trendLoading ? <PatientRecordSkeleton />
                        : trendError ? <Failure message={trendError} />
                          : trendEntries.length > 0 && (
                            <PatientVisualBoundary resetKey={`${selectedCode}:trend:${trendEntries.length}:${query}`}>
                              <PatientTrendVisual entries={trendEntries} dataType={selectedType.dataType} query={query} />
                            </PatientVisualBoundary>
                          )
                    )}
                  </div>
                </div>
              )}
            </main>
          </div>
        )}
      </motion.section>
      <AnimatePresence>
        {linking && (
          <PatientAppLinkDialog patient={person} onClose={() => setLinking(false)}
            onLinked={() => setAppAccountsRevision((revision) => revision + 1)} />
        )}
        {editing && (
          <PatientEditDialog patient={patient} onClose={() => setEditing(false)}
            onSaved={(saved) => {
              setEdited({ name: saved.name, email: saved.email, dateOfBirth: saved.dateOfBirth, gender: saved.gender });
              if (onEdited) onEdited();
            }} />
        )}
      </AnimatePresence>
    </div>
  );
}
